import React, { useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  useDndContext,
  MeasuringStrategy,
  DropAnimation,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import type {
  DragStartEvent,
  DragEndEvent,
  MeasuringConfiguration,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS, isKeyboardEvent } from '@dnd-kit/utilities';
import classNames from 'classnames';
import { Page, Layout, Position } from './Page';
import type { Props as PageProps } from './Page';
import styles from './Pages.module.css';
import pageStyles from './Page.module.css';
import { Grid } from '@mui/material';
import { ItemType, SelectedFacilityType } from '../types';
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { saveDashboardWidgetsSorting } from "../../../store/thunk/mainDashboardThunk"
import { TenantValueType } from '..';


interface Props {
  layout: Layout;
  items: ItemType[]
  setItems: React.Dispatch<React.SetStateAction<ItemType[]>>
  pinComponentHandler: (id: string) => void;
  selectedValueFacility?: string;
  facilityIds?: string;
  locationIds?: string;
  currentTenant: TenantValueType | undefined
}

const measuring: MeasuringConfiguration = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

const dropAnimation: DropAnimation = {
  keyframes({ transform }) {
    return [
      { transform: CSS.Transform.toString(transform.initial) },
      {
        transform: CSS.Transform.toString({
          scaleX: 0.98,
          scaleY: 0.98,
          x: transform.final.x - 10,
          y: transform.final.y - 10,
        }),
      },
    ];
  },
  sideEffects: defaultDropAnimationSideEffects({
    className: {
      active: pageStyles.active,
    },
  }),
};

export function Pages({ layout, items, setItems, pinComponentHandler, selectedValueFacility, facilityIds, locationIds, currentTenant }: Props) {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const dispatch = useAppDispatch();
  const { userData } = useAppSelector((state) => state.authReducer);
  const activeIndex = activeId != null ? items.findIndex((value) => value?.id === activeId) : -1;
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      sensors={sensors}
      collisionDetection={closestCenter}
    // measuring={measuring}
    >
      <SortableContext items={items}>
        <Grid className={classNames(styles.Pages)} container spacing={1} sx={{ marginTop: "-0.6rem", marginY: 1 }}>
          {items.map((item, index) => (
            <SortablePage
              id={item?.id}
              item={item}
              index={index + 1}
              key={item?.id}
              layout={layout}
              activeIndex={activeIndex}
              pinComponentHandler={pinComponentHandler}
              selectedValueFacility={selectedValueFacility}
              facilityIds={facilityIds}
              locationIds={locationIds}
              currentTenant={currentTenant}
            />
          ))}
        </Grid>
        {/* </ul> */}
      </SortableContext>
      {/* <DragOverlay dropAnimation={dropAnimation}>
        {activeId != null ? (
          <PageOverlay id={activeId} layout={layout} items={items} />
        ) : null}
      </DragOverlay> */}
    </DndContext>
  );

  function handleDragStart({ active }: DragStartEvent) {
    setActiveId(active.id);
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  function handleDragEnd({ over }: DragEndEvent) {
    if (over) {
      const overIndex = items.findIndex((value) => value?.id === over.id);

      if (activeIndex !== overIndex) {
        const newIndex = overIndex;
        const newItems = arrayMove(items, activeIndex, newIndex);
        setItems(newItems);
        dispatch(saveDashboardWidgetsSorting({ username: userData.username, dashboard_order: JSON.stringify(newItems) }));
      }
    }

    setActiveId(null);
  }
}

function PageOverlay({
  id,
  items,
  ...props
}: Omit<PageProps, 'index' | 'item'> & { items: ItemType[] }) {
  const { activatorEvent, over } = useDndContext();
  const isKeyboardSorting = isKeyboardEvent(activatorEvent);
  const activeIndex = items.findIndex((value) => value?.id === id);
  const overIndex = over?.id ? items.findIndex((value) => value?.id === over?.id) : -1;

  return (
    <Page
      id={id}
      {...props}
      clone
      insertPosition={
        isKeyboardSorting && overIndex !== activeIndex
          ? overIndex > activeIndex
            ? Position.After
            : Position.Before
          : undefined
      }
      item={items[activeIndex]}
    // Component={items[activeIndex]?.Component}

    />
  );
}

function SortablePage({
  id,
  activeIndex,
  ...props
}: PageProps & { activeIndex: number }) {
  const {
    attributes,
    listeners,
    index,
    isDragging,
    isSorting,
    over,
    setNodeRef,
    transform,
    transition,

  } = useSortable({
    id,
    animateLayoutChanges: always,
  });

  return (
    <Page
      ref={setNodeRef}
      id={id}
      active={isDragging}
      style={{
        transition,
        transform: /* isSorting ? undefined : */ CSS.Translate.toString(transform),
        zIndex: isDragging ? '99999 !important' : '0 !important'
      }}
      insertPosition={
        over?.id === id
          ? index > activeIndex
            ? Position.After
            : Position.Before
          : undefined
      }
      {...props}
      {...attributes}
      {...listeners}
    />
  );
}

function always() {
  return false;
}
